import { FC, useState } from 'react';
import { Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Badge, CustomButton, Text } from '../../shared/uiComponents';
import { InsuranceDetailProperties } from '../../redux/API/ClientAPIHelpers/insuranceProperties';
import InsuranceDetailModal from './InsuranceDetail';
import { getBranchBadgeType } from '../../shared/Helpers/constants';
import './index.scss';

interface MobileViewProps {
	insurances: InsuranceDetailProperties[] | any[];
}

const getInsuranceTypeLabel = (insurance: InsuranceDetailProperties): string => {
	if (insurance.parentId) return 'Sub-Insurance';
	if (insurance.isOutOfNetwork) return 'Out of Network';

	return 'Network';
};

const getInsuranceTypeColor = (insurance: InsuranceDetailProperties): 'success' | 'warning' | 'gray' => {
	if (insurance.parentId) return 'gray';
	if (insurance.isOutOfNetwork) return 'warning';

	return 'success';
};

const InsuranceCard: FC<{
	insurance: InsuranceDetailProperties;
	isSubInsurance?: boolean;
	subInsurances: InsuranceDetailProperties[];
}> = ({ insurance, subInsurances, isSubInsurance = false }) => {
	const [expanded, setExpanded] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const hasSubInsurances = subInsurances.length > 0;

	return (
		<>
			<div className={`insuranceCard ${isSubInsurance ? 'subInsuranceCard' : ''}`}>
				<div className="info">
					<div className="row">
						<Text title={insurance.name} type="psm" fontWeight="semiBold" />
					</div>
					<div className="row">
						<Text title="Status:" type="pxs" />
						<Badge
							label={insurance.isActive ? 'Active' : 'Inactive'}
							sizeType="sm"
							type={insurance.isActive ? 'success' : 'gray'}
						/>
					</div>
					<div className="row">
						<Text title="Type:" type="pxs" />
						<Badge label={getInsuranceTypeLabel(insurance)} sizeType="sm" type={getInsuranceTypeColor(insurance)} />
					</div>
					<div className="row">
						<Text title="Clients:" type="pxs" />
						<Text title={String(insurance.clientCount)} type="pxs" />
					</div>
					<div className="row">
						<Text title="Users:" type="pxs" />
						<Text title={String(insurance.userCount)} type="pxs" />
					</div>
					<div className="row">
						<Text title="Locations:" type="pxs" />
						<Text title={String(insurance.locationCount)} type="pxs" />
					</div>
					{insurance.branches && insurance.branches.length > 0 && (
						<div className="branchesRow">
							<Text title="Branches:" type="pxs" />
							<div className="badges">
								{insurance.branches.map((branch) => (
									<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
								))}
							</div>
						</div>
					)}
				</div>
				<div className="action">
					<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
						<CustomButton
							title="View Details"
							buttonType="secondaryGray"
							onClick={() => setModalOpen(true)}
							style={{ flex: 1 }}
						/>
						{hasSubInsurances && (
							<CustomButton
								title={expanded ? 'Hide Sub-Insurances' : 'Show Sub-Insurances'}
								buttonType="secondaryGray"
								onClick={() => setExpanded(!expanded)}
								iconOnly={expanded ? <ExpandLess /> : <ExpandMore />}
							/>
						)}
					</div>
				</div>
			</div>

			<InsuranceDetailModal insuranceId={insurance.id} open={modalOpen} onClose={() => setModalOpen(false)} />

			{hasSubInsurances && (
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					{subInsurances.map((subInsurance) => (
						<InsuranceCard key={subInsurance.id} insurance={subInsurance} subInsurances={[]} isSubInsurance />
					))}
				</Collapse>
			)}
		</>
	);
};

const MobileView: FC<MobileViewProps> = ({ insurances }) => {
	if (!insurances?.length) {
		return null;
	}

	// Filter out only parent insurances (no parentId)
	const parentInsurances = insurances.filter((ins: any) => !ins.parentId);

	// Create a map of parent IDs to their sub-insurances
	const subInsuranceMap = new Map<string, InsuranceDetailProperties[]>();
	insurances.forEach((ins: any) => {
		if (ins.parentId) {
			if (!subInsuranceMap.has(ins.parentId)) {
				subInsuranceMap.set(ins.parentId, []);
			}
			subInsuranceMap.get(ins.parentId)?.push(ins);
		}
	});

	return (
		<div>
			{parentInsurances.map((insurance: any) => (
				<InsuranceCard
					key={insurance.id}
					insurance={insurance}
					subInsurances={subInsuranceMap.get(insurance.id) || []}
				/>
			))}
		</div>
	);
};

export default MobileView;
